import { Link, useRouterState } from "@tanstack/react-router";
import { useAuth } from "@/components/providers/AuthProvider";
import {
  useFetchCurrentUserProfileQuery,
  useFetchUnreadNotificationsCountQuery,
  useCheckIsAdminQuery,
} from "@/api/clubsApi";
import { Icon } from "@/components/ui/Icon";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/Avatar";
import { userDisplayName } from "@tinyclub/shared/utils";
import { coreColors } from "@tinyclub/ui-primitives/colors";

interface NavItem {
  path: string;
  label: string;
  icon: string;
}

const navItems: NavItem[] = [
  {
    path: "/clubs",
    label: "Clubs",
    icon: "globe",
  },
  {
    path: "/events",
    label: "Events",
    icon: "calendar",
  },
  // {
  //   path: "/scenes",
  //   label: "Scenes",
  //   icon: "circles",
  // },
];

/**
 * AppSidebar - Floating icon-only vertical navigation.
 * - Fixed position, vertically centered on the left: main nav pill
 * - Separate fixed position at bottom-left: notification bell + profile avatar
 * - Icons only (no labels), with tooltip on hover
 */
export function AppSidebar() {
  const routerState = useRouterState();
  const currentPath = routerState.location.pathname;
  const { user } = useAuth();

  // Fetch user profile for avatar
  const { data: profile } = useFetchCurrentUserProfileQuery(user?.id ?? "", {
    skip: !user?.id,
  });

  // Fetch unread notifications count for badge
  const { data: unreadCount = 0 } = useFetchUnreadNotificationsCountQuery();

  // Check if user is admin (for moderation link)
  const { data: isAdmin } = useCheckIsAdminQuery(user?.id ?? "", {
    skip: !user?.id,
  });

  // Get initials for avatar fallback
  const getInitials = (name: string): string => {
    return name
      .split(" ")
      .map((part) => part[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const displayName = profile ? userDisplayName(profile) : user?.email ?? "";
  const avatarUrl = profile?.avatar_medium;

  return (
    <>
      {/* Main navigation pill - vertically centered */}
      <aside className="hidden lg:flex lg:fixed lg:left-4 lg:top-1/2 lg:-translate-y-1/2 lg:z-50">
        <nav className="flex flex-col gap-1 p-2 rounded-full bg-surface-1/80 backdrop-blur-md border border-border-tertiary shadow-lg">
          {navItems.map((item) => {
            const isActive = currentPath.startsWith(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`group relative w-10 h-10 flex items-center justify-center rounded-full transition-colors ${
                  isActive
                    ? "bg-surface-2 text-text-primary"
                    : "text-text-secondary hover:bg-surface-2 hover:text-text-primary"
                }`}
                aria-label={item.label}
              >
                <Icon name={item.icon} size={18} />
                {/* Tooltip */}
                <span className="absolute left-full ml-3 px-2 py-1 bg-background text-body-3 text-text-primary whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all pointer-events-none shadow-lg border border-border-tertiary rounded-full">
                  {item.label}
                </span>
              </Link>
            );
          })}
        </nav>
      </aside>

      {/* Bottom-left section: Notifications + Profile - separate from nav pill */}
      <aside className="hidden lg:flex lg:fixed lg:bottom-6 lg:left-4 lg:z-50">
        <div className="flex flex-col gap-2">
          {/* Moderation link (admin only) */}
          {isAdmin && (
            <Link
              to="/moderation"
              className={`group relative w-10 h-10 flex items-center justify-center rounded-full transition-colors ${
                currentPath === "/moderation"
                  ? "bg-surface-2 text-text-primary"
                  : "text-text-secondary hover:text-text-primary"
              }`}
              aria-label="Moderation"
            >
              <Icon name="shield" size={18} />
              {/* Tooltip */}
              <span className="absolute left-full ml-3 px-2 py-1 bg-background text-body-3 text-text-primary whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all pointer-events-none shadow-lg border border-border-tertiary rounded-full">
                Moderation
              </span>
            </Link>
          )}

          {/* Notification bell with unread badge */}
          <Link
            to="/notifications"
            className={`group relative w-10 h-10 flex items-center justify-center rounded-full transition-colors ${
              currentPath === "/notifications"
                ? "bg-surface-2 text-text-primary"
                : "text-text-secondary hover:text-text-primary"
            }`}
            aria-label="Notifications"
          >
            <Icon name="bell-outline" size={20} />
            {unreadCount > 0 && (
              <span className="absolute top-1.5 right-1.5 w-3.5 h-3.5 rounded-full bg-background flex items-center justify-center">
                <span
                  className="w-2.5 h-2.5 rounded-full"
                  style={{ backgroundColor: coreColors.red }}
                />
              </span>
            )}
            {/* Tooltip */}
            <span className="absolute left-full ml-3 px-2 py-1 bg-background text-body-3 text-text-primary whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all pointer-events-none shadow-lg border border-border-tertiary rounded-full">
              Notifications
            </span>
          </Link>

          {/* Profile avatar */}
          <Link
            to="/profile"
            className={`group relative w-10 h-10 flex items-center justify-center rounded-full transition-colors ${
              currentPath === "/profile"
                ? "bg-surface-2"
                : "hover:bg-surface-2"
            }`}
            aria-label="View profile"
          >
            <Avatar className="w-10 h-10 border-2 border-border-tertiary">
              {avatarUrl ? (
                <AvatarImage src={avatarUrl} alt={displayName} />
              ) : null}
              <AvatarFallback className="text-xs">
                {displayName ? getInitials(displayName) : ""}
              </AvatarFallback>
            </Avatar>
            {/* Tooltip */}
            <span className="absolute left-full ml-3 px-2 py-1 bg-background text-body-3 text-text-primary whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all pointer-events-none shadow-lg border border-border-tertiary rounded-full">
              Profile
            </span>
          </Link>
        </div>
      </aside>
    </>
  );
}
